import React, { useEffect, useMemo, useRef, useState } from 'react'
import { mockPatients as patientTrayPatients } from '@assets/mockdata/PatientTray/mockPatients'
import {
  Avatar,
  AVATAR_COLORS,
  AVATAR_MODES,
  type AvatarColor,
  type AvatarMode,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './AvatarShowcase.css'

const PATIENT_TRAY_PHOTO_FALLBACK =
  'https://cdn-icons-png.flaticon.com/128/9131/9131478.png'

const AVATAR_SAMPLE_PATIENTS = patientTrayPatients.map((patient) => ({
  id: patient.patient_id,
  name: `${patient.first_name} ${patient.last_name}`,
  src: patient.photo ?? PATIENT_TRAY_PHOTO_FALLBACK,
}))

const MODE_LABELS: Record<AvatarMode, string> = {
  solid: 'Solid',
  tinted: 'Tinted',
}

const formatColorLabel = (color: AvatarColor): string =>
  color
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

const AvatarShowcase: React.FC = () => {
  const [color, setColor] = useState<AvatarColor>('gray')
  const [mode, setMode] = useState<AvatarMode>('solid')
  const [initials, setInitials] = useState('EN')
  const [name, setName] = useState('')
  const [size, setSize] = useState(48)
  const [darkMode, setDarkMode] = useState(false)
  const [samplePatientId, setSamplePatientId] = useState(
    AVATAR_SAMPLE_PATIENTS[0]?.id ?? '',
  )
  const rowRefs = useRef<Partial<Record<AvatarColor, HTMLDivElement | null>>>(
    {},
  )

  const samplePatient = useMemo(
    () =>
      AVATAR_SAMPLE_PATIENTS.find(
        (patient) => patient.id === samplePatientId,
      ) ?? AVATAR_SAMPLE_PATIENTS[0],
    [samplePatientId],
  )

  useEffect(() => {
    rowRefs.current[color]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [color])

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'avatar-color',
            label: 'Highlighted color',
            control: (
              <select
                id="avatar-color"
                className="ui-field-control"
                value={color}
                onChange={(event) =>
                  setColor(event.target.value as AvatarColor)
                }
              >
                {AVATAR_COLORS.map((option) => (
                  <option key={option} value={option}>
                    {formatColorLabel(option)}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'avatar-mode',
            label: 'Mode',
            control: (
              <select
                id="avatar-mode"
                className="ui-field-control"
                value={mode}
                onChange={(event) => setMode(event.target.value as AvatarMode)}
              >
                {AVATAR_MODES.map((option) => (
                  <option key={option} value={option}>
                    {MODE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'avatar-size',
            label: 'Size (px)',
            control: (
              <input
                id="avatar-size"
                className="ui-field-control"
                type="number"
                min={32}
                max={64}
                step={4}
                value={size}
                onChange={(event) => setSize(Number(event.target.value) || 48)}
              />
            ),
          },
          {
            id: 'avatar-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="avatar-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
      {
        title: 'Content',
        fields: [
          {
            id: 'avatar-sample-patient',
            label: 'Sample patient photo',
            control: (
              <select
                id="avatar-sample-patient"
                className="ui-field-control"
                value={samplePatientId}
                onChange={(event) => setSamplePatientId(event.target.value)}
              >
                {AVATAR_SAMPLE_PATIENTS.map((patient) => (
                  <option key={patient.id} value={patient.id}>
                    {patient.name}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'avatar-initials',
            label: 'Initials',
            control: (
              <input
                id="avatar-initials"
                className="ui-field-control"
                type="text"
                maxLength={2}
                value={initials}
                onChange={(event) =>
                  setInitials(event.target.value.toUpperCase())
                }
              />
            ),
          },
          {
            id: 'avatar-name',
            label: 'Name (auto initials)',
            control: (
              <input
                id="avatar-name"
                className="ui-field-control"
                type="text"
                value={name}
                placeholder="e.g. Enamel AI"
                onChange={(event) => setName(event.target.value)}
              />
            ),
          },
        ],
      },
    ],
    [color, darkMode, mode, initials, name, samplePatientId, size],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Avatar"
        description="Solid and tinted color variants with initials, icon, and photo modes. Dark mode uses the tinted look."
        stageClassName="ui-preview__stage--wide avatar-showcase__stage-wrap"
      >
        <div
          className={`avatar-showcase__sheet${
            darkMode ? ' avatar-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Initials, icon, and photo variants using the panel settings.
              {darkMode ? ' Dark mode forces the tinted look.' : ''}
            </p>
            <div className="avatar-showcase__live-samples">
              <div className="avatar-showcase__sample">
                <Avatar
                  mode={mode}
                  color={color}
                  initials={initials}
                  name={name || undefined}
                  size={size}
                  darkMode={darkMode}
                />
                <span className="avatar-showcase__sample-label">Initials</span>
              </div>
              <div className="avatar-showcase__sample">
                <Avatar color="avatar" size={size} darkMode={darkMode} />
                <span className="avatar-showcase__sample-label">Icon</span>
              </div>
              {samplePatient ? (
                <div className="avatar-showcase__sample">
                  <Avatar
                    src={samplePatient.src}
                    alt={samplePatient.name}
                    size={size}
                    darkMode={darkMode}
                  />
                  <span className="avatar-showcase__sample-label">Photo</span>
                </div>
              ) : null}
            </div>
          </section>

          <div className="avatar-showcase__sheet-header">
            <span className="avatar-showcase__sheet-header-label">Color</span>
            {AVATAR_MODES.map((modeOption) => (
              <span
                key={modeOption}
                className="avatar-showcase__sheet-header-label avatar-showcase__sheet-header-label--mode"
              >
                {MODE_LABELS[modeOption]}
              </span>
            ))}
          </div>

          {AVATAR_COLORS.map((colorOption) => (
            <div
              key={colorOption}
              ref={(element) => {
                rowRefs.current[colorOption] = element
              }}
              className={`avatar-showcase__row${
                colorOption === color ? ' avatar-showcase__row--selected' : ''
              }${darkMode ? ' avatar-showcase__row--dark' : ''}`}
            >
              <span className="avatar-showcase__row-label">
                {formatColorLabel(colorOption)}
              </span>
              {AVATAR_MODES.map((modeOption) => (
                <div key={modeOption} className="avatar-showcase__cell">
                  <Avatar
                    mode={modeOption}
                    color={colorOption}
                    initials={colorOption === 'avatar' ? undefined : initials}
                    name={
                      colorOption === 'avatar' ? undefined : name || undefined
                    }
                    size={size}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel componentName="Avatar" sections={propertySections} />
    </>
  )
}

export default AvatarShowcase
